import { solitaire, type SolitaireRules } from '@cardengine/shared';
import { useStore } from '../store.js';

interface SolitaireRulesEditorProps {
  rules: SolitaireRules;
  editable: boolean;
}

const DRAW_OPTIONS: { value: SolitaireRules['drawCount']; label: string; hint: string }[] = [
  { value: 1, label: 'Turn one', hint: 'One card at a time. Most deals can be solved.' },
  { value: 3, label: 'Turn three', hint: 'Three at a time — only every third card is reachable.' },
];

const REDEAL_OPTIONS: { value: number | null; label: string; hint: string }[] = [
  { value: null, label: 'Unlimited', hint: 'Go through the stock as many times as you like.' },
  { value: 3, label: 'Three passes', hint: 'A middle ground between forgiving and brutal.' },
  { value: 0, label: 'One pass', hint: 'Through the stock once, and that is your lot.' },
];

interface ToggleProps {
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}

function Toggle({ label, hint, checked, disabled, onChange }: ToggleProps) {
  return (
    <label className={`rule ${disabled ? 'rule--locked' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="rule__body">
        <span className="rule__label">{label}</span>
        <span className="rule__hint">{hint}</span>
      </span>
    </label>
  );
}

/**
 * Klondike's settings, in the same shape as the Switch editor.
 *
 * Solitaire has far fewer knobs, so this is mostly the preset row — but it
 * reuses the same class names deliberately, so the lobby looks like one page
 * rather than two that happen to share a route.
 */
export function SolitaireRulesEditor({ rules, editable }: SolitaireRulesEditorProps) {
  const updateRules = useStore((s) => s.updateRules);

  const set = <K extends keyof SolitaireRules>(key: K, value: SolitaireRules[K]) => {
    if (editable) updateRules({ [key]: value } as Partial<SolitaireRules>);
  };

  const activePreset = solitaire.RULE_PRESETS.find(
    (preset) => JSON.stringify(preset.rules) === JSON.stringify(rules),
  );

  return (
    <section className="rules" aria-label="Solitaire settings">
      <header className="rules__header">
        <h2>Settings</h2>
      </header>

      <div className="presets">
        <span className="presets__label">Preset</span>
        <div className="presets__row">
          {solitaire.RULE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`presets__button ${activePreset?.id === preset.id ? 'is-active' : ''}`}
              disabled={!editable}
              onClick={() => editable && updateRules(preset.rules)}
              title={preset.description}
            >
              {preset.name}
            </button>
          ))}
        </div>
        <p className="presets__description">
          {activePreset ? activePreset.description : 'Custom settings.'}
        </p>
      </div>

      <div className="rules__ranks">
        <label className="rule rule--choice">
          <span className="rule__label">Stock</span>
          <select
            value={rules.drawCount}
            disabled={!editable}
            onChange={(event) => set('drawCount', Number(event.target.value) === 3 ? 3 : 1)}
          >
            {DRAW_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="rule__hint">
            {DRAW_OPTIONS.find((o) => o.value === rules.drawCount)?.hint}
          </span>
        </label>

        <label className="rule rule--choice">
          <span className="rule__label">Passes</span>
          <select
            value={rules.redeals === null ? 'unlimited' : String(rules.redeals)}
            disabled={!editable}
            onChange={(event) =>
              set('redeals', event.target.value === 'unlimited' ? null : Number(event.target.value))
            }
          >
            {REDEAL_OPTIONS.map((option) => (
              <option key={option.label} value={option.value === null ? 'unlimited' : String(option.value)}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="rule__hint">
            {REDEAL_OPTIONS.find((o) => o.value === rules.redeals)?.hint}
          </span>
        </label>
      </div>

      <div className="rules__toggles">
        <Toggle
          label="Take cards back off the foundations"
          hint="A banked card can come back down to the tableau if it rescues the position."
          checked={rules.allowFoundationToTableau}
          disabled={!editable}
          onChange={(value) => set('allowFoundationToTableau', value)}
        />
        <Toggle
          label="Any card fills an empty column"
          hint="Off is standard — normally only a King may open a gap."
          checked={rules.anyCardFillsEmptyColumn}
          disabled={!editable}
          onChange={(value) => set('anyCardFillsEmptyColumn', value)}
        />
      </div>
    </section>
  );
}
